import * as React from 'react'
import { DashboardLayout, DashboardContent, DashboardHeader } from '../layouts/dashboard-layout'
import { QuickSidebar, QuickSidebarProps } from './quick-sidebar'
import { MetricCard, MetricCardProps } from './metric-card'
import { PerformanceChart, PerformanceChartProps } from './performance-chart'
import { QuickActions, QuickActionsProps } from './quick-actions'
import { UserProfile, UserProfileProps } from './user-profile'
import { Button } from '../ui/button'
import { Search, Bell, Menu } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface SimpleDashboardProps {
  title: string
  subtitle?: string
  sidebar: Omit<QuickSidebarProps, 'title' | 'subtitle'> & {
    title?: string
    subtitle?: string
  }
  metrics?: MetricCardProps[]
  chart?: PerformanceChartProps
  quickActions?: QuickActionsProps
  user?: UserProfileProps['user']
  headerActions?: React.ReactNode
  showSearch?: boolean
  className?: string
}

export const SimpleDashboard = React.forwardRef<HTMLDivElement, SimpleDashboardProps>(
  ({ 
    title, 
    subtitle, 
    sidebar, 
    metrics = [], 
    chart, 
    quickActions,
    user,
    headerActions,
    showSearch = true,
    className, 
    ...props 
  }, ref) => {
    const dashboardHeader = (
      <DashboardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm" className="lg:hidden">
              <Menu className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-gradient-gold-purple text-xl font-bold">{title}</h1>
              {subtitle && (
                <p className="text-sm text-muted-foreground">{subtitle}</p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {showSearch && (
              <div className="hidden md:flex items-center space-x-2">
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <input
                    placeholder="Search..."
                    className="pl-8 pr-4 py-2 text-sm border rounded-md bg-background"
                  />
                </div>
              </div>
            )}
            {headerActions}
            <Button variant="outline" size="sm">
              <Bell className="h-4 w-4" />
            </Button>
            {user && <UserProfile user={user} compact />}
          </div>
        </div>
      </DashboardHeader>
    )

    const dashboardSidebar = (
      <QuickSidebar 
        title={sidebar.title || title}
        subtitle={sidebar.subtitle || subtitle}
        {...sidebar}
      />
    )

    return (
      <div ref={ref} className={className} {...props}>
        <DashboardLayout
          sidebar={dashboardSidebar}
          header={dashboardHeader}
        >
          <DashboardContent>
            {/* Metrics Grid */}
            {metrics.length > 0 && (
              <div className={cn(
                'grid gap-6 mb-6',
                metrics.length === 1 ? 'grid-cols-1' :
                metrics.length === 2 ? 'grid-cols-1 md:grid-cols-2' :
                metrics.length === 3 ? 'grid-cols-1 md:grid-cols-3' :
                'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
              )}>
                {metrics.map((metric, index) => (
                  <MetricCard key={index} {...metric} />
                ))}
              </div>
            )}

            {/* Performance Chart */}
            {chart && (
              <div className="mb-6">
                <PerformanceChart {...chart} />
              </div>
            )}

            {/* Quick Actions */}
            {quickActions && (
              <div className="mb-6">
                <QuickActions {...quickActions} />
              </div>
            )}
          </DashboardContent>
        </DashboardLayout>
      </div>
    )
  }
)

SimpleDashboard.displayName = 'SimpleDashboard'
